import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { Row } from 'components/shared/Layout';
import { CANCEL_SPECIFIC_BOOKING_ITEMS, REVERT_CHANGE, CONFIRM_BOOKING } from 'store/mutations';
import { ActionButton, CancelButton } from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import { isUserEurostarAgent } from 'utils/booking';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { StyledConfirmButton } from './ExtrasTrain.styles';
import { hasTrainSegment } from '../Extras.utils';

import ExtrasOnlyBooking from './ExtrasOnlyBooking';
import ExtrasInTrainSegment from './ExtrasInTrainSegment';
import { bookingStatus } from 'types/booking';

const ExtrasTrain = ({ user = {}, booking, history, currency, refetch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [itemsToCancel, setItemsToCancel] = useState([]);
  const [cancelSpecificBookingItems, { loading }] = useMutation(CANCEL_SPECIFIC_BOOKING_ITEMS);
  const [revertChange, { loading: revertLoading, error: revertError }] = useMutation(REVERT_CHANGE);
  const [confirmBooking, { loading: confirmLoading, error: confirmError }] = useMutation(CONFIRM_BOOKING);
  const isVoided = booking.displayStatus === bookingStatus.VOIDED;
  const isBookingUnderpaid = booking.status === bookingStatus.UNDERPAID;

  if (revertError) {
    throw new AgentPortalError(revertError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/booking/${booking.reference}/payments`
    });
  }

  if (confirmError) {
    revertChange({
      variables: {
        bookingReference: booking.reference
      }
    });

    throw new AgentPortalError(confirmError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/booking/${booking.reference}/payments`
    });
  }

  const needsRefund = Math.abs(booking.totalPriceToBePaid) > 0;

  const isExtrasOnlyBooking = !hasTrainSegment(booking) && Boolean(booking.outboundExtras.length || booking.inboundExtras.length);

  const handleCheckboxChange = (itemRefs, selectedCount = 0) => {
    if (itemsToCancel.find((i) => itemRefs.includes(i))) {
      let result = itemsToCancel.filter((item) => !itemRefs.includes(item));
      if (selectedCount) {
        result = [...result, ...itemRefs.slice(0, selectedCount)];
      }

      return setItemsToCancel(result);
    }

    return setItemsToCancel([...itemsToCancel, ...itemRefs]);
  };

  const handleCalculateRefund = async () => {
    await cancelSpecificBookingItems({
      variables: {
        bookingReference: booking.reference,
        itemRefs: itemsToCancel
      }
    });

    setIsOpen(true);
  };

  const handleRevert = async () => {
    await revertChange({
      variables: {
        bookingReference: booking.reference
      }
    });

    setIsOpen(false);
  };

  const handleConfirm = async () => {
    await confirmBooking({
      variables: {
        bookingReference: booking.reference
      }
    });

    if (needsRefund) {
      return history.push(`/manage/bookings/${booking.reference}/refund-methods?currency=${currency}`);
    }

    return setIsOpen(false);
  };

  return (
    <>
      {isExtrasOnlyBooking ? (
        <ExtrasOnlyBooking booking={booking} itemsToCancel={itemsToCancel} onCheckboxChange={handleCheckboxChange} refetch={refetch} />
      ) : (
        <ExtrasInTrainSegment
          history={history}
          isEurostarAgent={isUserEurostarAgent(user)}
          booking={booking}
          itemsToCancel={itemsToCancel}
          onCheckboxChange={handleCheckboxChange}
          refetch={refetch}
        />
      )}
      <Row span={2} start={2}>
        <ActionButton
          data-cy="calculate-refund"
          disabled={user.isReadOnly || !itemsToCancel.length || loading || isVoided || isBookingUnderpaid}
          onClick={handleCalculateRefund}
        >
          <FormattedMessage id="extras.calculateRefund" />
        </ActionButton>
      </Row>
      <Modal
        isOpen={isOpen}
        title={<FormattedMessage id="extras.confirmRefund" />}
        subtitle={<FormattedMessage id="extras.popupSubtitle" />}
        onClose={handleRevert}
      >
        <div>
          <FormattedMessage id="extras.refundAmountMessage" />:{' '}
          <strong>
            <FormattedCurrency currency={currency} amount={Math.abs(booking.totalPriceToBePaid)} />
          </strong>
        </div>

        <StyledConfirmButton onClick={handleConfirm} disabled={confirmLoading} loading={confirmLoading}>
          <FormattedMessage id="extras.confirmRefund" />
        </StyledConfirmButton>

        <CancelButton onClick={handleRevert} disabled={revertLoading}>
          <FormattedMessage id="extras.cancel" />
        </CancelButton>
      </Modal>
    </>
  );
};

ExtrasTrain.propTypes = {
  booking: bookingShape,
  history: historyShape,
  currency: PropTypes.string.isRequired,
  user: userShape,
  refetch: PropTypes.func.isRequired
};

export default ExtrasTrain;
